/**
 * NavLink Component
 * WHY: React Router NavLink with active state styling for sidebar navigation
 *
 * RECORD-MODE-PARAM-PERSISTENCE (2026-05-17): auto-appends ?rec=1 to the
 * `to` prop when the current URL has rec=1 — so record-mode survives a
 * multi-page sidebar walkthrough without needing per-call site changes.
 * No-op when not in record mode OR when `to` is a non-string Path object.
 */

import { NavLink as RouterNavLink, NavLinkProps as RouterNavLinkProps } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useRecordMode, withRecordParam } from "@/hooks/useRecordMode";

interface NavLinkProps extends RouterNavLinkProps {
  activeClassName?: string;
}

export function NavLink({
  className,
  activeClassName = "bg-sidebar-accent text-sidebar-accent-foreground font-medium",
  children,
  to,
  ...props
}: NavLinkProps) {
  const recordMode = useRecordMode();
  const augmentedTo = typeof to === "string" ? withRecordParam(to, recordMode) : to;
  return (
    <RouterNavLink
      to={augmentedTo}
      className={({ isActive }) =>
        cn(
          typeof className === "function" ? className({ isActive, isPending: false, isTransitioning: false }) : className,
          isActive && activeClassName
        )
      }
      {...props}
    >
      {children}
    </RouterNavLink>
  );
}
